<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Associação de produtos</p>
        </div>

        <Breadcrumb :items="breadcrumbList" class="mb-3" v-if="!loading"/>

        <div class="muven-content">
            <ResourceNotFound
                v-if="!loading && loadError"
                resource="canal"
                :identifier="$route.params.id"
                list-path="/canais"
                list-label="Voltar para canais"
                :kind="loadErrorKind"
                :error-message="loadErrorMessage"
                @retry="load" />

            <template v-if="!loadError">
                <div class="muven-tip muven-tip-standalone tip-info mb-3" v-show="!filterSubmitted">
                    <fa icon="info" class="tip-icon" />
                    <p>Preencha o nome do produto que deseja fazer a associação e clique em "Filtrar".</p>
                </div>

                <div class="muven-card filter-form-container">
                    <div class="muven-card-loading" v-show="submittingFilter">
                        <div class="muven-card-loading-message-container">
                            <p class="loading-message">
                                <fa class="loading-icon" icon="sync-alt" spin />
                            </p>
                        </div>
                    </div>

                    <div>
                        <div class="">
                            <div class="d-inline-block">
                                <label>Nome, código ou SKU do produto <span class="required">*</span></label>
                                <input type="text"
                                    class="form-control"
                                    v-model="filterForm.info">
                            </div>

                            <div class="d-inline-block ml-5">
                                <label>Status de associação</label>
                                <div class="btn-group">
                                    <button type="button" 
                                        class="btn"
                                        v-for="(associationStatus, index) in filterByAssociationStatus" :key="index"
                                        :class="{
                                            'btn-outline-secondary': !filterForm.isFilterByAssociationActive(associationStatus),
                                            'btn-secondary': filterForm.isFilterByAssociationActive(associationStatus),
                                        }"
                                        @click="filterForm.setFilterByAssociationStatus(associationStatus)">
                                        {{associationStatus.description}}
                                    </button>
                                </div>
                            </div>

                            <div class="d-inline-block ml-5">
                                <label>Status do produto</label>
                                <div class="btn-group">
                                    <button type="button" 
                                        class="btn"
                                        v-for="(status, index) in filterByStatus" :key="index"
                                        :class="{
                                            'btn-outline-secondary': !filterForm.isFilterByStatus(status),
                                            'btn-secondary': filterForm.isFilterByStatus(status),
                                        }"
                                        @click="filterForm.setFilterByStatus(status)">
                                        {{status.description}}
                                    </button>
                                </div>
                            </div>

                            <div class="row mt-3" v-show="hasMoreThanOneStore">
                                <div class="col-12">
                                    <label>Loja <span class="required">*</span></label>
                                    <multiselect
                                        v-model="filterForm.store"
                                        :options="storeList"
                                        :multiple="false"
                                        :show-labels="false"
                                        :allow-empty="false"
                                        @select="chooseFilterFormStore"
                                        placeholder=""
                                        label="name"
                                        track-by="id" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="muven-card-actions">
                        <button class="btn btn-primary" 
                            :disabled="!filterForm.isValid() || submittingFilter"
                            @click="submitFilter(filterForm)">
                                Filtrar

                                <Loading v-show="submittingFilter" />
                        </button>
                    </div>
                </div>
            
                <div class="muven-card association-card" 
                    v-for="(productAssociation, index) in productAssociationList" 
                    :key="index" 
                    :class="{
                        'product-association-complete': productAssociation.productBackend.hasAssociation()
                    }">
                
                    <div class="muven-card-loading" v-show="productAssociation.associationState.linkingProduct || productAssociation.associationState.unlinkingProduct">
                        <div class="muven-card-loading-message-container">
                            <p class="loading-message">
                                <fa class="loading-icon" icon="sync-alt" spin />
                            </p>
                        </div>
                    </div>

                    <div class="row justify-content-between">
                        <div class="col-6">
                            <div class="muven-card sticky-top">

                                <fa icon="check-circle" class="text-success association-success-icon" v-show="productAssociation.productBackend.hasAssociation()" />

                                <div class="muven-card-title">
                                    <p>Na retaguarda</p>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-12">
                                        <label>Nome</label>
                                        <p>{{productAssociation.productBackend.name}}</p>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-6">
                                        <label>SKU </label>
                                        <p class="mb-0">{{productAssociation.productBackend.skuCode | infoOrPlaceholder}}</p>
                                        <p>{{productAssociation.productBackend.codeSource | infoOrPlaceholder}}</p>
                                    </div>

                                    <div class="col-6">                                    
                                        <label>Id canal</label>
                                        <p>{{productAssociation.productBackend.idExternalChannel | infoOrPlaceholder}}</p>
                                    </div>
                                </div>

                                <div class="row mt-3" v-show="productAssociation.productBackend.hasVariations()">
                                    <div class="col">
                                        <label>{{productAssociation.productBackend.firstVariationName}}</label>
                                        <p>{{productAssociation.productBackend.firstVariationValue}}</p>
                                    </div>

                                    <div class="col" v-show="productAssociation.productBackend.hasMoreThanOneVariation()">
                                        <label>{{productAssociation.productBackend.secondVariationName}}</label>
                                        <p>{{productAssociation.productBackend.secondVariationValue}}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-6">
                            <div class="muven-card" v-if="!productAssociation.associationState.hasSearchedByChannel && !productAssociation.productBackend.hasAssociation()">

                                <fa icon="check-circle" class="text-success association-success-icon" v-show="productAssociation.productBackend.hasAssociation()" />

                                <div class="muven-card-loading" v-show="productAssociation.associationState.searchingInChannel">
                                    <div class="muven-card-loading-message-container">
                                        <p class="loading-message">
                                            <fa class="loading-icon" icon="sync-alt" spin />
                                        </p>
                                    </div>
                                </div>

                                <div class="muven-card-title">
                                    <p>No canal</p>
                                </div>

                                <div>
                                    <div>
                                        <label>{{searchTerms}}</label>
                                        <input type="text"
                                            v-model="productAssociation.associationState.productCode" 
                                            class="form-control" />
                                    </div>
                                </div>

                                <div class="muven-card-actions">
                                    <button class="btn btn-outline-primary"
                                        :disabled="!productAssociation.associationState.isValid() ||productAssociation.associationState.searchingInChannel"
                                        @click="searchInChannel(productAssociation)">
                                        Buscar

                                        <Loading v-if="productAssociation.associationState.searchingInChannel" />
                                    </button>
                                </div>
                            </div>

                            <div v-if="productAssociation.associationState.hasSearchedByChannel || productAssociation.productBackend.hasAssociation()">

                                <div class="muven-card mb-3" v-for="(productChannel, index) in productAssociation.productChannelList" :key="index">

                                    <fa icon="check-circle" class="text-success association-success-icon" v-show="productAssociation.productBackend.hasAssociation()" />

                                    <div class="muven-card-title" v-show="index === 0">
                                        <p>No canal</p>
                                    </div>

                                    <div class="mb-3">
                                        <div class="row mb-3">
                                            <div class="col-12">
                                                <label>Nome</label>
                                                <p>{{productChannel.name | infoOrPlaceholder}}</p>
                                            </div>
                                        </div>

                                        <div class="row">
                                            <div class="col-4">
                                                <label>SKU</label>
                                                <p>{{productChannel.skuCode | infoOrPlaceholder}}</p>
                                            </div>

                                            <div class="col-4">
                                                <label>Anúncio (item_id)</label>
                                                <p>{{productChannel.getItemId() | infoOrPlaceholder}}</p>
                                            </div>

                                            <div class="col-4">
                                                <label>Variação (model_id)</label>
                                                <p>{{productChannel.getModelId() | infoOrPlaceholder}}</p>
                                            </div>
                                        </div>

                                        <div class="row mt-3" v-show="productChannel.hasVariations()">
                                            <div class="col">
                                                <label>{{productChannel.firstVariationName}}</label>
                                                <p>{{productChannel.firstVariationValue}}</p>
                                            </div>

                                            <div class="col" v-show="productChannel.hasMoreThanOneVariation()">
                                                <label>{{productChannel.secondVariationName}}</label>
                                                <p>{{productChannel.secondVariationValue}}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="muven-card-actions">
                                        <div>
                                            <button class="btn btn-outline-primary"
                                                v-show="!productAssociation.associationState.productLinkSuccessfull && !productAssociation.productBackend.hasAssociation()"
                                                :disabled="productAssociation.associationState.linkingProduct"
                                                @click="linkProducts(productAssociation, productChannel)">
                                                Associar

                                                <Loading v-show="productAssociation.associationState.linkingProduct" />
                                            </button>

                                            <button class="btn btn-outline-danger"
                                                v-show="productAssociation.productBackend.hasAssociation()"
                                                :disabled="productAssociation.associationState.unlinkingProduct"
                                                @click="unlinkProducts(productAssociation)">
                                                Desfazer associação

                                                <fa icon="exclamation-triangle" v-show="!productAssociation.associationState.unlinkingProduct" />
                                                <Loading v-show="productAssociation.associationState.unlinkingProduct" />
                                            </button>

                                            <button class="btn btn-outline-secondary"
                                                v-if="productAssociation.associationState.hasSearchedByChannel && !productAssociation.productBackend.hasAssociation()"
                                                @click="goBackToFilter(productAssociation)">
                                                Voltar
                                            </button>
                                        </div>    
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <ListCount :amount="productAssociationList.length" v-show="!!productAssociationList.length" />

                <div class="muven-card" v-if="filterSubmitted && !submittingFilter && !productAssociationList.length">
                    <div class="">
                        <p>Nada encontrado com o filtro:</p>
                        <ul>
                            <li class="mb-2 mt-2">
                                <span class="text-muted">Nome, código ou SKU:</span> 
                                <span class="ml-3">{{filterFormSubmitted.info | infoOrPlaceholder}}</span>
                            </li>
                            <li class="mb-2">
                                <span class="text-muted">Status do produto:</span> 
                                <span class="ml-3">{{filterFormSubmitted.status.description}}</span>
                            </li>
                            <li>
                                <span class="text-muted">Status de associação:</span> 
                                <span class="ml-3">{{filterFormSubmitted.associationStatus.description}}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { ChannelModel } from '../models/ChannelModel';
import { infoOrPlaceholder } from '@/utils/filters/InformationOrPlaceholderFilter'
import productService from '@/product/services/ProductService'
import channelService from '../services/ChannelService';
import { toQueryString } from '@/utils/parsers/QueryString';
import { ChannelEnum } from '@c2b/muven-commons';
import { mapGetters, mapState } from 'vuex';
import { StoreModel } from '@/store/models/StoreModel';
import ResourceLoadError from '../../utils/mixins/ResourceLoadError'
import { getChannelNotFoundBreadcrumb } from '../utils/ChannelNotFoundBreadcrumb'

const associationStatus = {
    All: 0,
    Linked: 1,
    NotLinked: 2,
}

const statusEnum = {
    All: 0,
    Active: 1,
    Inactive: 2,
}

class FilterForm {
    static getFilterByStatus() {
        return [
                {
                    id: statusEnum.All,
                    description: 'Todos',
                },
                {
                    id: statusEnum.Active,
                    description: 'Ativos',
                },
                {
                    id: statusEnum.Inactive,
                    description: 'Inativos',
                },
            ]
    }

    static getFilterByAssociationStatus() {
        return [
                {
                    id: associationStatus.All,
                    description: 'Todos',
                },
                {
                    id: associationStatus.NotLinked,
                    description: 'Não associados',
                },
                {
                    id: associationStatus.Linked,
                    description: 'Associados',
                },
            ]
    }

    constructor() {
        this.info = ''
        this.status = FilterForm.getFilterByStatus()[0]
        this.associationStatus = FilterForm.getFilterByAssociationStatus()[1]
        this.store = new StoreModel()
    }

    isValid() {
        return !!this.info &&
            !!this.store && 
            !!this.store.id &&
            this.status !== undefined &&
            this.associationStatus !== undefined;            
    }

    setStore(store) {
        this.store = store
    }

    setFilterByAssociationStatus(assOption) {
        this.associationStatus = assOption
    }

    isFilterByAssociationActive(assOption) {
        return this.associationStatus.id === assOption.id
    }

    setFilterByStatus(statusOption) {
        this.status = statusOption
    }

    isFilterByStatus(statusOption) {
        return this.status.id === statusOption.id
    }

    toQueryString() {
        const objparam = {
            info: this.info,
            status: this.status.id,
            associationStatus: this.associationStatus.id,
        }

        return toQueryString(objparam)
    }
}

class SkuAssociation {
    constructor(productAssociable) {
        this.idParent = ''
        this.idMuven = ''
        this.idExternalChannel = ''
        // Id do anúncio (item_id) ao qual esta variação pertence. Sem ele não dá
        // para distinguir dois anúncios que têm variações com o mesmo nome.
        this.idParentExternalChannel = ''
        this.isActive = true
        this.name = ''
        this.skuCode = ''
        this.codeSource = ''
        this.eanCode = ''
        this.firstVariationName = ''
        this.firstVariationValue = ''
        this.secondVariationName = ''
        this.secondVariationValue = ''

        Object.assign(this, productAssociable)
    }

    hasAssociation() {
        return !!this.idMuven && !!this.idExternalChannel;   
    }

    hasParent() {
        return !!this.idParent
    }

    hasVariations() {
        return !!this.firstVariationName || !!this.secondVariationName; 
    }

    hasMoreThanOneVariation() {
        return !!this.firstVariationName && !!this.secondVariationName;
    }

    /**
     * Id do anúncio (item_id). A associação é gravada como `item_id+model_id`,
     * então aceitamos tanto a chave composta quanto o id do pai recebido do canal.
     */
    getItemId() {
        const parts = String(this.idExternalChannel || '').split('+')
        // Produto simples: o próprio idExternalChannel já é o item_id.
        return parts.length > 1 ? parts[0] : (this.idParentExternalChannel || this.idParent || this.idExternalChannel || '')
    }

    /** Id da variação (model_id). Vazio quando o produto do canal é simples. */
    getModelId() {
        const parts = String(this.idExternalChannel || '').split('+')
        if (parts.length > 1) {
            return parts[1]
        }
        return (this.idParentExternalChannel || this.idParent) ? this.idExternalChannel : ''
    }
}

function isNotFoundError(err) {
    const messageNotFound = !!err && !!err.message && (
        err.message.indexOf('404') > -1 ||
        err.message.toLowerCase().indexOf('not found') > -1 ||
        err.message.toLowerCase().indexOf('not_found') > -1
    );
    const statusNotFound = !!err && err.status === '404';
    return messageNotFound || statusNotFound;
}

/**
 * Procura a variação com o idExternalChannel informado no produto retornado pelo canal.
 * Quando o canal devolve o próprio SKU sem variações, o produto é a variação.
 */
function findChannelVariation(productInChannel, idExternalChannel) {
    if (!productInChannel) {
        return null
    }

    const variation = (productInChannel.variations || []).find((v) => String(v.idExternalChannel) === String(idExternalChannel))
    if (variation) {
        return variation
    }

    if (!productInChannel.hasVariations() && String(productInChannel.idExternalChannel) === String(idExternalChannel)) {
        return productInChannel
    }

    return null
}

function buildChannelSku(productInChannel, channelVariation) {
    const firstGrid = channelVariation.getFirstGridInfo ? channelVariation.getFirstGridInfo() : null;
    const secondGrid = channelVariation.getSecondGridInfo ? channelVariation.getSecondGridInfo() : null;

    return new SkuAssociation({
        idParent: productInChannel.idExternalChannel,
        name: productInChannel.name,
        isActive: productInChannel.isActive,
        idMuven: channelVariation.idMuven,
        idExternalChannel: channelVariation.idExternalChannel,
        skuCode: channelVariation.skuCode,
        eanCode: channelVariation.eanCode,
        firstVariationName: firstGrid ? firstGrid.name : '',
        firstVariationValue: firstGrid ? firstGrid.value : '',
        secondVariationName: secondGrid ? secondGrid.name : '',
        secondVariationValue: secondGrid ? secondGrid.value : '',
    })
}

class ProductAssociationState {
    constructor() {
        this.productCode = ''
        this.hasSearchedByChannel = false
        this.searchingInChannel = false
        this.productLinkSuccessfull = false
        this.productUnlinkSuccessfull = false
        this.linkingProduct = false
        this.unlinkingProduct = false
    }

    isValid() {
        return !!this.productCode
    }
}

export default {
    name: 'ChannelProductListAssociationView',
    data() {
        return {
            loading: true,
            submittingFilter: false,
            filterSubmitted: false,
            channel: new ChannelModel(),
            filterForm: new FilterForm(),
            filterFormSubmitted: new FilterForm(),
            filterByStatus: FilterForm.getFilterByStatus(),
            filterByAssociationStatus: FilterForm.getFilterByAssociationStatus(),
            productAssociationList: [],
            breadcrumbList: [],            
        }
    },
    filters: {
        infoOrPlaceholder,
    },
    mixins: [
        ResourceLoadError,
    ],
    mounted() {
        this.load()
    },
    methods: {
        async load() {
            this.loading = true
            this.loadError = null

            try {
                const channelId = this.$route.params.id
                this.channel = await channelService.getChannelById(channelId)        

                if (this.storeList.length === 1) {
                    this.filterForm.setStore(this.storeList[0])    
                }

                this.breadcrumbList = [
                    {
                        id: 0,
                        description: 'Canais',
                        path: '/canais'
                    },
                    {
                        id: 1,
                        description: `Configurações ${this.channel.name}`,
                        channel: this.channel,
                        path: `/canais/${channelId}/configuracoes`,
                    },
                    {
                        id: 2,
                        description: `Associação de produtos Muven x ${this.channel.name}`,
                        path: '',
                    },
                ];
            } catch(e) {
                this.loadError = e
                this.breadcrumbList = getChannelNotFoundBreadcrumb(this.$route.params.id)
            }

            this.loading = false
        },
        async submitFilter(form) {
            this.productAssociationList = [];

            this.filterSubmitted = true;
            this.submittingFilter = true;
            this.filterFormSubmitted = cloneDeep(form);

            let firstGrid;
            let secondGrid;

            try {
                const productBackendList = await productService.getAssociableProductListInBackend(this.filterFormSubmitted.store.id, this.channel.id, this.filterFormSubmitted.toQueryString())

                for (const prodBackend of productBackendList) {
                    let productChannel = new SkuAssociation()

                    if (!prodBackend.hasVariations()) {
                        if (prodBackend.hasAssociation()) {
                            try {
                                productChannel = await productService.getAssociableProductInChannelByIdExternalChannel(this.filterFormSubmitted.store.id, this.channel.id, prodBackend.idExternalChannel)
                            } catch (err) {
                                if (isNotFoundError(err)) {
                                    // Não desfaz a associação automaticamente: uma falha no agente
                                    // apagaria associações válidas. Só o usuário desfaz, pelo botão.
                                    productChannel = new SkuAssociation({
                                        idExternalChannel: prodBackend.idExternalChannel,
                                    })

                                    this.$floatingAlert.info({
                                        message: `Não foi possível encontrar o produto com código <b>${prodBackend.idExternalChannel}</b> no canal. A associação com o produto <b>${prodBackend.name}</b> foi mantida; verifique e desfaça manualmente se necessário.`,
                                    })
                                } else {
                                    throw err
                                }
                            }
                        }

                        if (!productChannel.hasVariations()) {
                            this.productAssociationList.push({
                                productBackend: new SkuAssociation({
                                        idParent: null,
                                        name: prodBackend.name,
                                        isActive: prodBackend.isActive,
                                        idMuven: prodBackend.idMuven,
                                        idExternalChannel: prodBackend.idExternalChannel,
                                        skuCode: prodBackend.skuCode,
                                        codeSource: prodBackend.codeSource,
                                        eanCode: prodBackend.eanCode,
                                        firstVariationName: null,
                                        firstVariationValue: null,
                                        secondVariationName: null,
                                        secondVariationName: null,
                                }),
                                productChannelList: [
                                    new SkuAssociation({
                                        name: productChannel.name,
                                        idExternalChannel: productChannel.idExternalChannel,
                                        skuCode: productChannel.skuCode,
                                        eanCode: productChannel.eanCode,
                                        isActive: productChannel.isActive,
                                        firstVariationName: null,
                                        firstVariationValue: null,
                                        secondVariationName: null,
                                        secondVariationName: null,
                                    })
                                ],
                                associationState: new ProductAssociationState(),
                            })
                        } else {
                            this.productAssociationList.push({
                                productBackend: new SkuAssociation({
                                        idParent: null,
                                        name: prodBackend.name,
                                        isActive: prodBackend.isActive,
                                        idMuven: prodBackend.idMuven,
                                        idExternalChannel: prodBackend.idExternalChannel,
                                        skuCode: prodBackend.skuCode,
                                        codeSource: prodBackend.codeSource,
                                        eanCode: prodBackend.eanCode,
                                        firstVariationName: null,
                                        firstVariationValue: null,
                                        secondVariationName: null,
                                        secondVariationName: null,
                                }),
                                productChannelList: productChannel.variations.map((v) => {
                                    // prodBackend has no variations, but the productChannel has, 
                                    // so use the idExternalChannel, skuCode, eanCode and isActive from the variation
                                    // instead of the parent product.
                                    firstGrid = v.getFirstGridInfo();
                                    secondGrid = v.getSecondGridInfo();

                                    return new SkuAssociation({
                                        name: productChannel.name,
                                        idExternalChannel: v.idExternalChannel,
                                        idParentExternalChannel: productChannel.idExternalChannel,
                                        skuCode: v.skuCode,
                                        eanCode: v.eanCode,
                                        isActive: v.isActive,
                                        firstVariationName: firstGrid ? firstGrid.name : '',
                                        firstVariationValue: firstGrid ? firstGrid.value : '',
                                        secondVariationName: secondGrid ? secondGrid.name : '',
                                        secondVariationValue: secondGrid ? secondGrid.value : '',
                                    })
                                }),
                                associationState: new ProductAssociationState(),
                            })
                        }
                    } else {
                        let tmpProductChannel = null;

                        if (prodBackend.hasAssociation()) {
                            try {
                                tmpProductChannel = await productService.getAssociableProductInChannelByIdExternalChannel(this.filterFormSubmitted.store.id, this.channel.id, prodBackend.idExternalChannel)
                            } catch (err) {
                                // Se o pai não for encontrado, cada variação ainda é consultada pelo próprio id abaixo.
                                if (!isNotFoundError(err)) {
                                    throw err
                                }
                            }
                        }

                        const variationsNotFound = []

                        // If the product has variations, then pushes each variation
                        // to the association list. By doing so, we make sure each
                        // variation can be matched with some info from the channel.
                        for (const variation of prodBackend.variations) {
                            // Cada variação começa sem dados do canal, para não herdar os da anterior.
                            productChannel = new SkuAssociation()

                            if (variation.hasAssociation()) {
                                let channelVariation = findChannelVariation(tmpProductChannel, variation.idExternalChannel)
                                let productInChannel = tmpProductChannel

                                // Alguns canais (ex.: WBuy) devolvem só um SKU por consulta, então a
                                // variação pode não vir junto com o pai: consulta pelo próprio id.
                                if (!channelVariation) {
                                    try {
                                        productInChannel = await productService.getAssociableProductInChannelByIdExternalChannel(this.filterFormSubmitted.store.id, this.channel.id, variation.idExternalChannel)
                                        channelVariation = findChannelVariation(productInChannel, variation.idExternalChannel)
                                    } catch (err) {
                                        if (!isNotFoundError(err)) {
                                            throw err
                                        }
                                    }
                                }

                                if (channelVariation) {
                                    productChannel = buildChannelSku(productInChannel, channelVariation)
                                } else {
                                    // Sem retorno do canal, mostra ao menos o id associado no Muven.
                                    productChannel = new SkuAssociation({
                                        idExternalChannel: variation.idExternalChannel,
                                    })
                                    variationsNotFound.push(variation)
                                }
                            }

                            firstGrid = variation.getFirstGridInfo();
                            secondGrid = variation.getSecondGridInfo();

                            const association = {
                                productBackend: new SkuAssociation({
                                    idParent: prodBackend.idMuven,
                                    name: prodBackend.name,
                                    isActive: prodBackend.isActive,
                                    idMuven: variation.idMuven,
                                    idExternalChannel: variation.idExternalChannel,
                                    skuCode: variation.skuCode,
                                    codeSource: variation.codeSource,
                                    eanCode: variation.eanCode,
                                    firstVariationName: firstGrid ? firstGrid.name : '',
                                    firstVariationValue: firstGrid ? firstGrid.value : '',
                                    secondVariationName: secondGrid ? secondGrid.name : '',
                                    secondVariationValue: secondGrid ? secondGrid.value : '',
                                }),
                                productChannelList: [cloneDeep(productChannel)],
                                associationState: new ProductAssociationState(),
                            }

                            this.productAssociationList.push(association)
                        }

                        if (variationsNotFound.length) {
                            const description = variationsNotFound.map((v) => {
                                const grids = [v.getFirstGridInfo(), v.getSecondGridInfo()].filter((g) => !!g).map((g) => g.value).join(' / ')
                                return `<b>${grids || v.skuCode}</b> (id ${v.idExternalChannel})`
                            }).join(', ')

                            this.$floatingAlert.info({
                                message: `Variações do produto <b>${prodBackend.name}</b> associadas no Muven, mas não encontradas no canal: ${description}. As associações foram mantidas.`
                            })
                        }
                    }
                }
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })                
            }

            this.submittingFilter = false
        },
        async searchInChannel(productAssociation) {
            productAssociation.associationState.searchingInChannel = true
            productAssociation.productChannelList = []

            try {          
                const productInChannelList = await productService.getAssociableProductInChannelByAnyInfo(this.filterFormSubmitted.store.id, this.channel.id, productAssociation.associationState.productCode)

                if (!productInChannelList.length) {
                    throw new Error(`Não encontramos nenhum produto em ${this.channel.name} com a informação <b>"${productAssociation.associationState.productCode}"</b>.`)
                }

                for (const productInChannel of productInChannelList) {
                    if (!productInChannel.hasVariations()) {
                        productAssociation.productChannelList.push(new SkuAssociation({
                            idParent: null,
                            name: productInChannel.name,
                            isActive: productInChannel.isActive,
                            idMuven: null,
                            idExternalChannel: productInChannel.idExternalChannel,
                            skuCode: productInChannel.skuCode,
                            eanCode: productInChannel.eanCode,
                            firstVariationName: null,
                            firstVariationValue: null,
                            secondVariationName: null,
                            secondVariationValue: null,
                        }))
                    } else {
                        productInChannel.variations.forEach((variation) => {
                            const firstGrid = variation.getFirstGridInfo();
                            const secondGrid = variation.getSecondGridInfo();

                            productAssociation.productChannelList.push(new SkuAssociation({
                                idParent: productInChannel.idExternalChannel,
                                name: productInChannel.name,
                                isActive: productInChannel.isActive,
                                idMuven: null,
                                idExternalChannel: variation.idExternalChannel,
                                skuCode: variation.skuCode,
                                eanCode: variation.eanCode,
                                firstVariationName: firstGrid ? firstGrid.name : '',
                                firstVariationValue: firstGrid ? firstGrid.value : '',
                                secondVariationName: secondGrid ? secondGrid.name : '',
                                secondVariationValue: secondGrid ? secondGrid.value : '',
                            }))
                        })
                    }
                }

                productAssociation.associationState.hasSearchedByChannel = true  
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            productAssociation.associationState.searchingInChannel = false
        },
        async linkProducts(productAssociation, productChannel) {
            if (productAssociation.productBackend.hasVariations() && !productChannel.hasVariations()) {
                this.$floatingAlert.error({
                    message: `Não é possível associar um produto de grade no Muven com um produto simples no canal. Se o produto tem grade no Muven ele também deve ter grade no canal para que a associação funcione corretamente.`
                })

                return;
            }

            productAssociation.associationState.linkingProduct = true

            productAssociation.productChannelList = productAssociation.productChannelList.filter((pc) => {
                return productChannel.idExternalChannel === pc.idExternalChannel
            })

            let associationList = [
                // Simple product on Muven and simple product on channel
                {
                    idMuven: productAssociation.productBackend.idMuven,
                    idExternalChannel: productChannel.idExternalChannel,
                }
            ]
            
            if (productAssociation.productBackend.hasVariations() && productChannel.hasVariations()) {
                // First position is the parent info
                // Second is the variation info
                associationList = [
                    {
                        idMuven: productAssociation.productBackend.idParent,
                        idExternalChannel: productChannel.idParent,
                    },
                    {
                        idMuven: productAssociation.productBackend.idMuven,
                        idExternalChannel: productChannel.idExternalChannel,
                    }
                ]
            } else if (!productAssociation.productBackend.hasVariations() && productChannel.hasVariations()) {
                if (!this.channel.isMercadoLivre() && !this.channel.isWooCommerce() && !this.channel.isShopee() && !this.channel.isNuvemShop()) {
                    if (!this.channel.isTray()) {
                        associationList = [
                            {
                                idMuven: productAssociation.productBackend.idMuven,
                                idExternalChannel: productChannel.idExternalChannel,
                            }
                        ]
                    } else {
                        associationList = [
                            {
                                idMuven: productAssociation.productBackend.idMuven,
                                idExternalChannel: `${productChannel.idExternalChannel}_V`,
                            }
                        ]                       
                    }
                } else {
                    associationList = [
                        // Since the product is flat on Muven and we need to know the idExternalChannel,
                        // we concat both the id from the parent and the child, as in: parent+child
                        {
                            idMuven: productAssociation.productBackend.idMuven,
                            idExternalChannel: `${productChannel.idParent}+${productChannel.idExternalChannel}`,
                        }
                    ]
                }
            }

            try {
                await productService.updateProductAssociation(this.filterFormSubmitted.store.id, this.channel.id, associationList)

                productAssociation.productBackend.idExternalChannel = productChannel.idExternalChannel
                productAssociation.associationState.productLinkSuccessfull = true

                this.$floatingAlert.success({
                    message: 'Associação feita com sucesso!'
                })
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message || `Houve um problema no momento da associação dos produtos.`
                })
            }

            productAssociation.associationState.linkingProduct = false
        },
        async unlinkProducts(productAssociation) {
            productAssociation.associationState.unlinkingProduct = true

            const associationList = [
                {
                    idMuven: productAssociation.productBackend.idMuven,
                    idExternalChannel: null,
                }
            ]

            try {
                await productService.updateProductAssociation(this.filterFormSubmitted.store.id, this.channel.id, associationList)
                productAssociation.productBackend.idExternalChannel = null
                productAssociation.productChannelList = [new SkuAssociation()]
                productAssociation.associationState.productUnlinkSuccessfull = true
                productAssociation.associationState.hasSearchedByChannel = false

                this.$floatingAlert.success({
                    message: 'Associação removida com sucesso!'
                })
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message || `Houve um problema no momento de desfazer a associação entre os produtos.`
                })
            }

            productAssociation.associationState.unlinkingProduct = false
        },
        chooseFilterFormStore(store) {
            this.filterForm.store = store
        },
        goBackToFilter(productAssociation) {
            productAssociation.associationState.hasSearchedByChannel = false
        },
    },
    computed: {
        ...mapState({
            storeList: (state) => { 
                return StoreModel.getStoreListWithIdAndName(state.storeList)
            }
        }),
        ...mapGetters([
            'hasMoreThanOneStore',
        ]),
        searchTerms() {
            if (this.channel.isShopee()) {
                // A Shopee só busca por nome; os ids são resolvidos direto no anúncio.
                return 'Nome do produto, item_id ou item_id+model_id'
            }

            if (this.channel.isShopify() || this.channel.isNuvemShop()) {
                return 'Nome do produto'
            }

            if (this.channel.isMercadoLivre()) {
                return 'Código SKU ou nome do produto'
            }

            if (this.channel.isTray()) {
                return 'Código de referência'
            }

            if (this.channel.isMagazineLuiza()) {
                return 'ID do SKU'
            }

            if (this.channel.isShein()) {
                return 'Código SPU na Shein'
            }

            return 'Código SKU ou referência'
        }
    },
}
</script>

<style lang="scss" scoped>
.filter-form-container {
    margin-bottom: 15px;

    .btn {
        font-size: .8em;
        min-height: 38px;
    }

    @media(min-width: 1210px) { 
        .btn {
            font-size: 1em;
            min-height: auto;
        }
    }
}

.association-card {
    margin-bottom: 15px;

    &.product-association-complete {
        .muven-card {
            .association-success-icon {
                position: absolute;
                top: 8px;
                right: 8px;
                font-size: 1.5em;
                z-index: 1;
                background-color: var(--surface-1);
                border-radius: 100%;            
            }
        }
    }

    .muven-card {
        position: relative;
        overflow: auto;
        background-color: var(--surface-2);

        &.sticky-top {
            position: sticky;
            top: 15px;
        }

        .muven-card-title {
            background-color: var(--surface-2);
            border-bottom: none;   
        }

        .muven-card-actions {
            border: none;
            border-top: 1px dashed var(--border);
        }

        border: 1px dashed var(--border);
    }
}
</style>